import axios from 'axios';
import { API_BASE_URL } from '../constants';

// Capacitor HTTP 플러그인 동적 import
let CapacitorHttp = null;
let Capacitor = null;
const isCapacitor = !!(window.Capacitor || window.capacitor);

// Capacitor HTTP 플러그인 로드
if (isCapacitor) {
  try {
    import('@capacitor/core').then(({ CapacitorHttp: CH, Capacitor: Cap }) => {
      CapacitorHttp = CH;
      Capacitor = Cap;
      console.log('✅ Capacitor HTTP 플러그인 로드 완료');
    }).catch(err => {
      console.warn('⚠️ Capacitor HTTP 플러그인 로드 실패:', err);
    });
  } catch (error) {
    console.warn('⚠️ Capacitor HTTP import 실패:', error);
  }
}

// 플랫폼 감지 함수
const getPlatformHeaders = () => {
  const headers = {};
  
  if (isCapacitor && Capacitor) {
    const platform = Capacitor.getPlatform();
    const isNative = Capacitor.isNativePlatform();
    
    if (isNative && (platform === 'ios' || platform === 'android')) {
      headers['X-Platform'] = platform;
      headers['X-Client-Type'] = 'native';
    } else {
      headers['X-Platform'] = 'web';
      headers['X-Client-Type'] = 'web';
    }
  } else {
    headers['X-Platform'] = 'web';
    headers['X-Client-Type'] = 'web';
  }
  
  return headers;
};

// HTTP 요청 함수
const makeRequest = async (config) => {
  // iOS Capacitor 환경에서는 CapacitorHttp 사용
  if (isCapacitor && CapacitorHttp) {
    try {
      const fullUrl = `${config.baseURL || API_BASE_URL}${config.url || ''}`;
      
      console.log('📱 Capacitor HTTP 요청:', {
        url: fullUrl,
        method: config.method || 'GET'
      });

      const response = await CapacitorHttp.request({
        url: fullUrl,
        method: config.method || 'GET',
        headers: {
          'Content-Type': 'application/json',
          'ngrok-skip-browser-warning': 'true',
          ...getPlatformHeaders(),
          ...config.headers
        },
        data: config.data,
        params: config.params
      });

      // axios와 호환되는 응답 형태로 변환
      return {
        data: response.data,
        status: response.status,
        statusText: response.status >= 200 && response.status < 300 ? 'OK' : 'Error',
        headers: response.headers,
        config: config
      };
    } catch (error) {
      console.error('❌ Capacitor HTTP 요청 실패:', error);
      // Capacitor HTTP 실패 시 axios로 폴백
      return await axios(config);
    }
  } else {
    // 웹 환경에서는 기존 axios 사용 (플랫폼 헤더 포함)
    const configWithPlatform = {
      ...config,
      headers: {
        ...getPlatformHeaders(),
        ...config.headers
      }
    };
    return await axios(configWithPlatform);
  }
};

// axios 인스턴스 생성 (웹 환경용)
const axiosInstance = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'ngrok-skip-browser-warning': 'true',
    ...getPlatformHeaders()
  }
});

// API 객체 생성 (axios와 동일한 인터페이스 제공)
const api = {
  // GET 요청
  get: (url, config = {}) => makeRequest({
    method: 'GET',
    url,
    baseURL: API_BASE_URL,
    ...config
  }),
  
  // POST 요청
  post: (url, data, config = {}) => makeRequest({
    method: 'POST',
    url,
    data,
    baseURL: API_BASE_URL,
    ...config
  }),
  
  // PUT 요청
  put: (url, data, config = {}) => makeRequest({
    method: 'PUT',
    url,
    data,
    baseURL: API_BASE_URL,
    ...config
  }),
  
  // DELETE 요청
  delete: (url, config = {}) => makeRequest({
    method: 'DELETE',
    url,
    baseURL: API_BASE_URL,
    ...config
  }),
  
  // PATCH 요청
  patch: (url, data, config = {}) => makeRequest({
    method: 'PATCH',
    url,
    data,
    baseURL: API_BASE_URL,
    ...config
  }),
  
  // 일반 요청
  request: (config) => makeRequest({
    baseURL: API_BASE_URL,
    ...config
  }),

  // axios 인스턴스 호환성 (기존 코드 지원)
  defaults: axiosInstance.defaults,
  interceptors: axiosInstance.interceptors
};

// 디버깅 정보 출력
console.log('🔧 API Service 초기화:', {
  isCapacitor,
  baseURL: API_BASE_URL,  
  capacitorHttpAvailable: !!CapacitorHttp
});

export default api; 